/**
 * Controller for Figure Tags and Updates
 * Handles tag management and figure update submissions/reviews
 */
class FigureTagController {
  constructor(apiService) {
    this.api = apiService;
  }

  // ==================== TAG OPERATIONS ====================

  /**
   * Create a new tag
   * @param {Object} data - Tag data
   * @param {string} data.name - Tag name
   * @param {string} [data.description] - Tag description
   * @returns {Promise<Object>} Created tag
   */
  async createTag(data) {
    const response = await this.api.post('/figures/tags', data);
    return response.data;
  }

  /**
   * Get all tags
   * @returns {Promise<Array>} List of all tags
   */
  async getAllTags() {
    const response = await this.api.get('/figures/tags');
    return response.data;
  }

  /**
   * Get popular tags
   * @param {number} [limit=20] - Number of tags to return
   * @returns {Promise<Array>} List of popular tags
   */
  async getPopularTags(limit = 20) {
    const response = await this.api.get('/figures/tags/popular', { params: { limit } });
    return response.data;
  }

  async searchTags(q, limit = 20) {
    const response = await this.api.get('/figures/tags/search', { params: { q, limit } });
    return response.data;
  }

  /**
   * Add tags to a figure
   * @param {string} figureId - Figure ID
   * @param {Array<string>} tags - Array of tag names
   * @returns {Promise<Object>} Updated figure
   */
  async addTagsToFigure(figureId, tags) {
    const response = await this.api.post(`/figures/${figureId}/tags`, { tags });
    return response.data;
  }

  /**
   * Remove tags from a figure
   * @param {string} figureId - Figure ID
   * @param {Array<string>} tags - Array of tag names to remove
   * @returns {Promise<Object>} Updated figure
   */
  async removeTagsFromFigure(figureId, tags) {
    const response = await this.api.delete(`/figures/${figureId}/tags`, { data: { tags } });
    return response.data;
  }

  /**
   * Get tags for a specific figure
   * @param {string} figureId - Figure ID
   * @returns {Promise<Array>} List of figure tags
   */
  async getFigureTags(figureId) {
    const response = await this.api.get(`/figures/${figureId}/tags`);
    return response.data;
  }

  /**
   * Search figures by tags
   * @param {Object} searchParams - Search parameters
   * @param {Array<string>} searchParams.tags - Array of tag names
   * @param {string} [searchParams.matchMode='any'] - 'all' or 'any'
   * @returns {Promise<Array>} List of matching figures
   */
  async searchFiguresByTags(searchParams) {
    const response = await this.api.post('/figures/search/tags', searchParams);
    return response.data;
  }

  /**
   * Delete a tag (admin only)
   * @param {string} tagId - Tag ID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteTag(tagId) {
    const response = await this.api.delete(`/figures/tags/${tagId}`);
    return response.data;
  }

  // ==================== FIGURE UPDATE OPERATIONS ====================

  /**
   * Submit a figure update for review
   * @param {Object} updateData - Update data
   * @param {string} updateData.figureId - Figure ID to update
   * @param {string} [updateData.name] - Updated name
   * @param {string} [updateData.company] - Updated company
   * @param {string} [updateData.category] - Updated category
   * @param {string} [updateData.classification] - Updated classification
   * @param {string} [updateData.origin] - Updated origin
   * @param {string} [updateData.character] - Updated character
   * @param {string} [updateData.material] - Updated material
   * @param {Array<string>} [updateData.artists] - Updated artists
   * @param {number} [updateData.price] - Updated price
   * @param {string} [updateData.priceCurrency] - Updated price currency
   * @param {string} [updateData.version] - Updated version
   * @param {string} [updateData.scale] - Updated scale
   * @param {number} [updateData.height] - Updated height
   * @param {number} [updateData.width] - Updated width
   * @param {number} [updateData.length] - Updated length
   * @param {string} [updateData.unit] - Updated unit
   * @param {string} [updateData.releaseDate] - Updated release date
   * @param {string} [updateData.description] - Updated description
   * @param {Array<string>} [updateData.tags] - Updated tags
   * @param {string} [updateData.submissionNote] - Note about the submission
   * @returns {Promise<Object>} Submitted update
   */
  async submitFigureUpdate(updateData) {
    const response = await this.api.post('/figures/updates/submit', updateData);
    return response.data;
  }

  /**
   * Review a figure update (admin/moderator only)
   * @param {Object} reviewData - Review data
   * @param {string} reviewData.updateId - Update history ID
   * @param {string} reviewData.status - 'approved' or 'rejected'
   * @param {string} [reviewData.rejectionReason] - Reason if rejected
   * @returns {Promise<Object>} Reviewed update
   */
  async reviewFigureUpdate(reviewData) {
    const response = await this.api.post('/figures/updates/review', reviewData);
    return response.data;
  }

  /**
   * Get all pending updates (admin/moderator only)
   * @returns {Promise<Array>} List of pending updates
   */
  async getPendingUpdates() {
    const response = await this.api.get('/figures/updates/pending');
    return response.data;
  }

  /**
   * Get my submitted updates
   * @returns {Promise<Array>} List of user's submitted updates
   */
  async getMyUpdates() {
    const response = await this.api.get('/figures/updates/my');
    return response.data;
  }

  /**
   * Get update history for a figure
   * @param {string} figureId - Figure ID
   * @param {Object} [options] - Query options
   * @param {boolean} [options.includePending=true] - Include pending updates
   * @param {boolean} [options.includeApproved=true] - Include approved updates
   * @param {boolean} [options.includeRejected=false] - Include rejected updates
   * @returns {Promise<Array>} List of updates
   */
  async getUpdateHistory(figureId, options = {}) {
    const response = await this.api.get(`/figures/${figureId}/updates/history`, { params: options });
    return response.data;
  }

  /**
   * Cancel a pending update
   * @param {string} updateId - Update ID
   * @returns {Promise<Object>} Cancellation result
   */
  async cancelUpdate(updateId) {
    const response = await this.api.delete(`/figures/updates/${updateId}`);
    return response.data;
  }
}

export default FigureTagController;
